import { Injectable } from '@angular/core';
import { ProcessActionItem } from 'src/app/components/processActionItem';
import {
  ACTION_MODULE_MAP,
  resolveActionLoader,
} from '../../components/actions/action-component.map';

@Injectable({ providedIn: 'root' })
export class ProcessService {
  private actionName!: string;
  private actionId!: number;
  private voucherType!: string;
  private listOfActionPermissions: any;

  setActionName(
    actionName: string,
    actionId: number,
    voucherType: string,
    listOfActionPermissions: any,
  ) {
    this.actionName = actionName;
    this.actionId = actionId;
    this.voucherType = voucherType;
    this.listOfActionPermissions = listOfActionPermissions;
    // [ACTIONS-DEBUG] Permission payload carried into the action window
    console.log('[ACTIONS-DEBUG] setActionName', {
      actionName,
      actionId,
      voucherType,
      permissionCount: Array.isArray(listOfActionPermissions)
        ? listOfActionPermissions.length
        : listOfActionPermissions,
    });
  }

  /**
   * Map keys sharing a word with the unmapped action name — turns a dead-end
   * error into "you meant this one" without needing the DB open.
   */
  private closestKeys(actionName: string): string[] {
    const words = actionName
      .toLowerCase()
      .split(/\s+/)
      .filter((w) => w.length > 2);
    if (!words.length) return [];

    return Object.keys(ACTION_MODULE_MAP)
      .map((key) => {
        const lower = key.toLowerCase();
        return { key, score: words.filter((w) => lower.includes(w)).length };
      })
      .filter((c) => c.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map((c) => c.key);
  }

  async getProcessName(): Promise<ProcessActionItem[]> {
    // [ACTIONS-DEBUG] Action mapping lookup
    const match = resolveActionLoader(this.actionName);
    console.log('[ACTIONS-DEBUG] mapping lookup', {
      actionName: this.actionName,
      actionId: this.actionId,
      loaderFound: !!match,
      mappedKeysCount: Object.keys(ACTION_MODULE_MAP).length,
    });

    if (!match) {
      // JSON.stringify exposes the whitespace an unquoted log would hide.
      console.error(
        'No module mapped for action:',
        JSON.stringify(this.actionName),
        '\nClosest map keys:',
        this.closestKeys(this.actionName),
      );
      return [];
    }

    if (match.matchedKey !== this.actionName) {
      console.warn(
        '[ACTIONS-DEBUG] action name matched only after normalizing —',
        `DB name ${JSON.stringify(this.actionName)}`,
        `vs map key ${JSON.stringify(match.matchedKey)}.`,
        'Fix the action name in Manage Actions.',
      );
    }

    const loader = match.loader;

    // [ACTIONS-DEBUG] Dynamic import / component resolution
    let component;
    try {
      ({ component } = await loader());
      console.log('[ACTIONS-DEBUG] dynamic import resolved', {
        actionName: this.actionName,
        componentName: component?.name,
      });
    } catch (e) {
      console.error('[ACTIONS-DEBUG] dynamic import FAILED', this.actionName, e);
      throw e;
    }

    return [
      new ProcessActionItem(
        component,
        this.actionName,
        this.actionId,
        this.voucherType,
        this.listOfActionPermissions,
      ),
    ];
  }
}
